import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Grid,
  Icon,
  TextField,
  IconButton,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ThemeProvider,
  createTheme,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { showAlert } from "components/CommonComponent/ShowAlert";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

import { autoCompleteProps } from "components/CommonComponent/CommonFunction";
import { getProductList } from "actions/product";
import { productList } from "constants/product";
import { getProjectList } from "actions/project";
import { projectList } from "constants/project";
import { getUnitListing } from "actions/unit";
import { unitList } from "constants/unit";
import { stationList } from "constants/station";
import { getStationList } from "actions/station";
import { subActivityList } from "constants/subActivity";
import { getSubActivityList } from "actions/subActivity";
import { getMicroActivityList } from "actions/microActivity";
import { microActivityList } from "constants/microActivity";
import { getRemarkList } from "actions/remark";
import { remarkList } from "constants/remark";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import {
  getTimeSheetById,
  deleteLhbRecordById,
  updateRecordsById,
  updateTimesheetStatus,
} from "actions/timesheet";
import { useDispatch, useSelector } from "react-redux";
import { urlDecodedId } from "components/CommonComponent/encodeDecodeFunction";
import { addLhbRecord } from "actions/timesheet";

const activityOptions = [
  { label: "Fresh", value: "FRESH" },
  { label: "Rework", value: "REWORK" },
];
const initialFields = {
  projectNumber: {
    value: "",
    error: "",
  },
  specification: {
    value: "",
    error: "",
  },
  typeOfProduct: {
    value: "",
    error: "",
  },
  unitNo: {
    value: "",
    error: "",
  },
  stationName: {
    value: "",
    error: "",
  },
  subActivity: {
    value: "",
    error: "",
  },
  microActivity: {
    value: "",
    error: "",
  },
  hours: {
    value: "",
    error: "",
  },
  date: {
    value: "",
    error: "",
  },
  shift: {
    value: "1",
    error: "",
  },
  lhbNo: {
    value: "",
    error: "",
  },
  remark: {
    value: "",
    error: "",
  },
};

function LHBDataDetails() {
  const { id } = useParams();
  const decodedId = urlDecodedId(id);
  const dispatch = useDispatch();

  //product
  const productListData = useSelector((state) => state.product.productList);
  //project
  const projectListData = useSelector((state) => state.project.projectList);
  //station data
  const stationListData = useSelector((state) => state.station.stationList);
  //sub and micro
  const microActivityData = useSelector(
    (state) => state.microActivity.microActivityList,
  );
  const subActivityData = useSelector(
    (state) => state.subActivity.subActivityList,
  );
  //unit
  const unitListingData = useSelector((state) => state.unit.unitList);
  //reamrk
  const remarkListData = useSelector((state) => state.remark.remarkList);
  const { notify } = useNotify();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditRoute = location.pathname.includes("/edit");
  const isViewMode = location.pathname.includes("/view");

  const [timeSheetData, setTimeSheetData] = useState(initialFields);
  const [employeeInfo, setEmployeeInfo] = useState();
  const [editingId, setEditingId] = useState(null);
  const [editedRow, setEditedRow] = useState({});

  const [tableRows, setTableRows] = useState([]);
  const [openPicker, setOpenPicker] = useState({
    id: null,
    field: null,
  });
  const [isEditEnabled, setIsEditEnabled] = useState(isEditRoute);
  const [loading, setLoading] = useState(false);
  const [originalRow, setOriginalRow] = useState(null);

  const [status, setStatus] = useState("PENDING");

  const products = productListData?.data?.Product || [];
  const units = unitListingData?.data?.Unit || [];
  const projects = projectListData?.data?.project || [];
  const stations = stationListData?.data?.station || [];
  const remarkslist = remarkListData?.data?.remarks || [];
  const subActivitiesList = subActivityData?.data?.subActivities || [];
  const microActivitesList = microActivityData?.data?.microActivities || [];

  useEffect(() => {
    if (!decodedId) return;
    getTimeSheetRecordsDetail(decodedId);
  }, [decodedId]);

  useEffect(() => {
    return () => {
      dispatch({ type: productList, payload: {} });
      dispatch({ type: projectList, payload: {} });
      dispatch({ type: unitList, payload: {} });
      dispatch({ type: stationList, payload: {} });
      dispatch({ type: subActivityList, payload: {} });
      dispatch({ type: microActivityList, payload: {} });
      dispatch({ type: remarkList, payload: {} });
    };
  }, [dispatch]);

  const getTimeSheetRecordsDetail = async (id) => {
    setLoading(true);
    try {
      const res = await getTimeSheetById(id);

      if (res?.code === 200) {
        const data = res?.data || {};
        const records = data?.lhbRecords ?? [];
        setEmployeeInfo({
          empNo: data?.technician?.employeeID || "",
          name: data?.technician?.name || "",
          department: data?.technician?.department?.name || "",
          departmentId: data?.technician?.department?.ID || null,
        });

        setTableRows(normalizeTableRows(records));
        setStatus(data?.status || "PENDING");
      } else {
        notify(res?.message || "Failed to fetch the details", "error");
      }
    } catch (error) {
      notify(error?.message || "Failed to fetch the details", "error");
    } finally {
      setLoading(false);
    }
  };

  const isPending = status === "PENDING";

  useEffect(() => {
    const today = new Date();
    const formattedDate =
      today.getFullYear() +
      "-" +
      String(today.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(today.getDate()).padStart(2, "0");

    setTimeSheetData((prev) => ({
      ...prev,
      date: { value: formattedDate, error: "" },
      shift: { value: "1", error: "" },
    }));
  }, []);

  const getOptimalHours = (hourString) => {
    if (!hourString) return "";
    const [h, m] = String(hourString).split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return "";
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  const normalizeTableRows = (rows = []) =>
    rows.map((row, index) => {
      const duration = row.durationMinutes || 0;
      // Convert durationMinutes to HH:mm
      const hours = `${String(Math.floor(duration / 60)).padStart(2, "0")}:${String(
        duration % 60,
      ).padStart(2, "0")}`;
      // If optimalHours is 0, null, or undefined, use the same value as hours
      const optimalHours =
        row.optimalHours && row.optimalHours > 0
          ? minutesToHHMM(row.optimalHours)
          : hours;

      return {
        id: row.ID,
        srNo: index + 1,

        product: row.product?.type || "",
        productId: row.product?.ID || null,

        projectNumber: row.projectID?.projectNo || "",
        projectId: row.projectID?.ID || null,

        unitNo: row.Unit?.unitNo || "",
        unitId: row.Unit?.ID || null,

        stationId: row.Station?.ID || null,
        stationName: row.Station?.name || "",

        subActivity: row.SubActivity?.ID || null,
        subActivityName: row.SubActivity?.name || "",

        microActivity: row.MicroActivity?.ID || null,
        microActivityName: row.MicroActivity?.name || "",

        hours,
        optimalHours,

        activityType: row.activityType || "",
        remark: row.customRemark || row.Remark?.remark || "",
        customRemark: row.customRemark || "",
        remarkId: row.Remark?.ID || null,
      };
    });

  const loadAllMasterLists = async () => {
    const deptId = employeeInfo?.departmentId;
    await Promise.all([
      getProductList(dispatch),
      getProjectList(dispatch),
      // getUnitListing(dispatch),

      getStationList(
        dispatch,
        null,
        deptId
          ? JSON.stringify([
              {
                field: "DeptID",
                condition: "eq",
                filterValues: [String(deptId)],
              },
            ])
          : "",
      ),
      getRemarkList(dispatch),
    ]);
  };

  const handleEdit = async (row) => {
    setEditingId(row.id);
    setLoading(true);

    await loadAllMasterLists();
    if (row.product) {
      const productId = row.productId;

      if (productId) {
        await getProjectList(
          dispatch,
          null,
          JSON.stringify([
            {
              field: "ProductID",
              condition: "eq",
              filterValues: [String(productId)],
            },
          ]),
        );
      }
    }

    if (row.stationId) {
      await getSubActivityList(
        dispatch,
        null,
        JSON.stringify([
          {
            field: "StationID",
            condition: "eq",
            filterValues: [String(row.stationId)],
          },
        ]),
      );
    }
    if (row.subActivity) {
      await getMicroActivityList(
        dispatch,
        null,
        JSON.stringify([
          {
            field: "SubActivityID",
            condition: "eq",
            filterValues: [String(row.subActivity)],
          },
        ]),
      );
    }
    setEditedRow({
      id: row.id,
      product: row.product,
      projectNumber: row.projectNumber,
      projectId: row.projectId,

      unitNo: row.unitNo,
      unitId: row.unitId,

      availableUnits: row.unitId
        ? [{ ID: row.unitId, unitNo: row.unitNo }]
        : [],

      stationId: row.stationId,
      stationName: row.stationName,

      subActivity: row.subActivity,
      microActivity: row.microActivity,

      hours: row.hours,
      optimalHours: row.optimalHours,

      activityType: row.activityType,
      remark: row.remark,

      customRemark: row.customRemark || "",
      remarkId: row.remarkId || null,
    });

    setOriginalRow(row);
    setLoading(false);
  };

  const handleCancelEdit = () => {
    if (editedRow?.isNew) {
      setTableRows((prev) => prev.filter((row) => row.id !== editedRow.id));
    }

    setEditingId(null);
    setEditedRow({});
    setOriginalRow(null);
  };

  const convertToMinutes = (time) => {
    if (!time) return 0;
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
  };

  const minutesToHHMM = (mins) => {
    if (mins === null || mins === undefined) return "";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  const handleSave = async () => {
    try {
      if (editedRow?.isNew) {
        if (
          !editedRow.productId ||
          !editedRow.projectId ||
          !editedRow.unitId ||
          !editedRow.stationId
        ) {
          notify("Please fill all required fields", "error");
          return;
        }

        const payload = {
          //need to pass timesheet id also
          timesheetID: decodedId,
          productID: editedRow.productId,
          projectID: editedRow.projectId,
          unitID: editedRow.unitId,
          //subunit
          stationID: editedRow.stationId,
          subActivityID: editedRow.subActivity,
          microActivityID: editedRow.microActivity,
          durationMinutes: convertToMinutes(editedRow.hours),
          optimalHours: convertToMinutes(editedRow.optimalHours),
          activityType: editedRow.activityType,
          remarkID: editedRow.remarkId,
          status: "PENDING",
        };

        try {
          const res = await addLhbRecord(payload);

          if (res?.code === 200 || res?.status === "SUCCESS") {
            notify(res?.message || "Row added successfully", "success");
            await getTimeSheetRecordsDetail(decodedId);
          } else {
            const message = Array.isArray(res?.message)
              ? res.message
                  .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
                  .join(", ")
              : res?.message || "Failed to save lhb record";

            notify(message, "error");
          }
        } catch (err) {
          notify(err?.message || "Error adding lhb record", "error");
        }

        return;
      }
      const isSame =
        originalRow.product === editedRow.product &&
        originalRow.projectNumber === editedRow.projectNumber &&
        originalRow.unitNo === editedRow.unitNo &&
        originalRow.stationName === editedRow.stationName &&
        String(originalRow.subActivity) === String(editedRow.subActivity) &&
        String(originalRow.microActivity) === String(editedRow.microActivity) &&
        originalRow.hours === editedRow.hours &&
        (originalRow.optimalHours || "") === (editedRow.optimalHours || "") &&
        (originalRow.remark || "") === (editedRow.remark || "");

      if (isSame) {
        notify("No changes detected", "info");
        setEditingId(null);
        setEditedRow({});
        setOriginalRow(null);
        return;
      }
      const payload = {
        productID: editedRow.productId,
        projectID: editedRow.projectId,
        unitID: editedRow.unitId,
        stationID: editedRow.stationId,
        subActivityID: editedRow.subActivity,
        microActivityID: editedRow.microActivity,
        durationMinutes: convertToMinutes(editedRow.hours),
        optimalHours: convertToMinutes(
          editedRow.optimalHours || getOptimalHours(editedRow.hours),
        ),
        activityType: editedRow.activityType,
        remarkID: editedRow.remarkId,
      };

      const res = await updateRecordsById(editedRow.id, payload);

      if (res?.code === 200 || res?.status === "SUCCESS") {
        notify(res?.message || "Row updated successfully", "success");
        setTableRows((prev) =>
          prev.map((r) =>
            r.id === editedRow.id
              ? {
                  ...r,
                  ...editedRow,
                  hours: editedRow.hours,
                  optimalHours:
                    editedRow.optimalHours ||
                    minutesToHHMM(payload.optimalMinutes),
                }
              : r,
          ),
        );

        setEditingId(null);
        setEditedRow({});
        setOriginalRow(null);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to save record";

        notify(message, "error");
      }
    } catch (error) {
      notify(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong",
        "error",
      );
    }
  };

  const handleFieldChangeTable = async (field, value) => {
    let updated = { ...editedRow, [field]: value };

    if (field === "product") {
      updated.product = value?.type || "";
      updated.productId = value?.ID || null;

      updated.projectNumber = "";
      updated.projectId = null;
      updated.unitNo = "";
      updated.unitId = null;

      if (value?.ID) {
        await getProjectList(
          dispatch,
          null,
          JSON.stringify([
            {
              field: "ProductID",
              condition: "eq",
              filterValues: [String(value.ID)],
            },
          ]),
        );
      }
    }
    if (field === "project") {
      updated.projectNumber = value?.projectNo || "";
      updated.projectId = value?.ID || null;

      updated.unitNo = "";
      updated.unitId = null;

      updated.availableUnits = value?.units || [];
    }

    if (field === "unitNo") {
      updated.unitNo = value?.unitNo || "";
      updated.unitId = value?.ID || null;
    }

    if (field === "stationName") {
      updated.stationName = value?.name || "";
      updated.stationId = value?.ID || null;

      updated.subActivity = null;
      updated.microActivity = null;

      if (value?.ID) {
        await getSubActivityList(
          dispatch,
          null,
          JSON.stringify([
            {
              field: "StationID",
              condition: "eq",
              filterValues: [String(value.ID)],
            },
          ]),
        );
      }
    }

    if (field === "subActivity") {
      updated.subActivity = value;
      updated.microActivity = null;

      if (value) {
        await getMicroActivityList(
          dispatch,
          null,
          JSON.stringify([
            {
              field: "SubActivityID",
              condition: "eq",
              filterValues: [String(value)],
            },
          ]),
        );
      }
    }

    if (field === "microActivity") {
      updated.microActivity = value;
    }

    if (field === "hours") {
      updated.optimalHours = getOptimalHours(value);
    }

    if (field === "remark") {
      updated.remark = value?.remark || "";
      updated.remarkId = value?.ID || null;
    }
    setEditedRow(updated);
  };

  const handleDeleteRow = (row) => {
    showAlert({
      title: "Are you sure you want to delete this entry?",
      message: `Entry will be deleted permanently.`,
      type: "warning",
      showCancel: true,
      confirmText: "Yes, Delete",
      cancelText: "Cancel",

      onConfirm: async () => {
        try {
          const res = await deleteLhbRecordById(row.id);

          if (res?.code === 200 || res?.status === "SUCCESS") {
            notify("Record deleted successfully", "success");

            setTableRows((prev) => prev.filter((r) => r.id !== row.id));
          } else {
            const message = Array.isArray(res?.message)
              ? res.message
                  .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
                  .join(", ")
              : res?.message || "Failed to delete";

            notify(message, "error");
          }
        } catch (error) {
          notify(
            error?.response?.data?.message ||
              error?.message ||
              "Something went wrong",
            "error",
          );
        }
      },
    });
  };

  const columns = [
    { key: "srNo", label: "Sr No", flex: 0.5 },
    { key: "product", label: "Product", flex: 0.9 },
    { key: "projectNumber", label: "Project No", flex: 1.1 },
    { key: "unitNo", label: "Unit No", flex: 1 },
    { key: "stationName", label: "Station", flex: 1.2 },
    { key: "subActivityName", label: "Sub Activity", flex: 1.5 },
    { key: "microActivityName", label: "Micro Activity", flex: 1.5 },
    { key: "hours", label: "Hours", flex: 1 },

    { key: "optimalHours", label: "Optimal Hours", flex: 1.2 },

    { key: "activityType", label: "Activity Type", flex: 1 },
    { key: "remark", label: "Remark", flex: 1.5 },

    ...(!isViewMode || isEditEnabled
      ? [{ key: "action", label: "Action", flex: 1 }]
      : []),
  ];
  const totalFlex = columns.reduce((sum, col) => sum + col.flex, 0);
  const totalHours = tableRows.reduce((sum, row) => {
    if (!row.hours) return sum;
    const [h, m] = row.hours.split(":").map(Number);
    return sum + h + m / 60;
  }, 0);

  const formattedTotal = (() => {
    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  })();

  const handleTimeChange = (newValue) => {
    if (!newValue) {
      setEditedRow((prev) => ({
        ...prev,
        hours: "",
        optimalHours: "",
      }));
      return;
    }
    const hours = newValue.hour();
    const minutes = newValue.minute();
    const formattedHours = `${String(hours).padStart(2, "0")}:${String(
      minutes,
    ).padStart(2, "0")}`;
    const optimal = getOptimalHours(formattedHours);
    setEditedRow((prev) => ({
      ...prev,
      hours: formattedHours,
      optimalHours: optimal,
    }));
  };
  const theme = createTheme({
    components: {
      MuiTimePicker: {
        styleOverrides: {
          root: {
            "& .MuiClock-root": {
              color: "#000",
            },
            "& .MuiPickersClockNumber-root": {
              color: "#000",
            },
          },
        },
      },
    },
  });

  const handleApprove = () => {
    const hasEmptyOptimalHours = tableRows.some(
      (row) => !row.optimalHours || row.optimalHours.trim() === "",
    );
    if (hasEmptyOptimalHours) {
      notify("Please fill the optimal hours for all entries", "error");
      return;
    }
    showAlert({
      type: "info",
      title: "Approve LHB Entry",
      message: "Are you sure you want to approve this LHB?",
      showCancel: true,
      confirmText: "Yes, Approve",
      cancelText: "Cancel",

      onConfirm: async () => {
        try {
          const payload = {
            status: "APPROVED",
          };

          const res = await updateTimesheetStatus(decodedId, payload);

          if (res?.code === 200 || res?.status === "SUCCESS") {
            notify("LHB approved successfully", "success");
            navigate("/admin/lhbData");
          } else {
            notify(res?.message || "Failed to approve", "error");
          }
        } catch (error) {
          notify(error?.message || "Something went wrong", "error");
        }
      },
    });
  };

  const handleReject = () => {
    showAlert({
      type: "warning",
      title: "Reject LHB Entry",
      message: "Please provide a remark.",
      showCancel: true,
      requireInput: true,
      inputLabel: "Rejection Remark",
      confirmText: "Yes, Reject",
      cancelText: "Cancel",

      onConfirm: async (remark) => {
        try {
          const payload = {
            status: "REJECTED",
            rejectionRemark: remark,
          };
          const res = await updateTimesheetStatus(decodedId, payload);
          if (res?.code === 200 || res?.status === "SUCCESS") {
            notify("LHB rejected successfully", "success");
            navigate("/admin/lhbData");
          } else {
            notify(res?.message || "Failed to reject", "error");
          }
        } catch (error) {
          notify(error?.message || "Something went wrong", "error");
        }
      },
    });
  };

  const isOptimalValid = (actual, optimal) => {
    if (!actual || !optimal) return true;
    const [ah, am] = actual.split(":").map(Number);
    const [oh, om] = optimal.split(":").map(Number);
    const actualMinutes = ah * 60 + am;
    const optimalMinutes = oh * 60 + om;
    return optimalMinutes <= actualMinutes;
  };

  const handleAddRow = async () => {
    await loadAllMasterLists(); // load dropdowns

    const newRow = {
      id: `new-${Date.now()}`,
      isNew: true,

      product: "",
      productId: null,

      projectNumber: "",
      projectId: null,

      unitNo: "",
      unitId: null,
      availableUnits: [],

      stationName: "",
      stationId: null,

      subActivity: null,
      microActivity: null,

      hours: "",
      optimalHours: "",

      remark: "",
      remarkId: null,
    };

    setTableRows((prev) => [newRow, ...prev]);
    setEditingId(newRow.id);
    setEditedRow(newRow);
    setOriginalRow(null);
  };
  return (
    <DashboardLayout>
      {/* <DashboardNavbar /> */}

      <MDBox>
        <Grid container>
          <Grid item xs={12}>
            <MDBox mb={1} p={2} pb={0}>
              <MDTypography variant="body1" fontWeight="medium">
                {employeeInfo?.name} ({employeeInfo?.empNo})
              </MDTypography>
            </MDBox>

            <Card sx={{ p: 2, pb: 0, mt: 2 }}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <IconButton
                    size="small"
                    onClick={() => navigate(-1)}
                    sx={{ p: 0.5 }}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>

                  <MDTypography variant="h6" fontWeight="bold">
                    {isEditEnabled ? "Edit TimeSheet" : "TimeSheet"}
                  </MDTypography>
                </MDBox>

                {(!isViewMode || isEditEnabled) && (
                  <MDButton
                    variant="contained"
                    color="primary"
                    onClick={handleAddRow}
                    disabled={!!editingId}
                  >
                    Add New Entry
                  </MDButton>
                )}
              </MDBox>

              {loading ? (
                <MDBox display="flex" justifyContent="center" py={5}>
                  <i className="fa fa-spinner fa-spin fa-2xl" />
                </MDBox>
              ) : tableRows.length === 0 ? (
                <MDBox display="flex" justifyContent="center" py={5}>
                  <MDTypography variant="body2" color="text">
                    No LHB records found.
                  </MDTypography>
                </MDBox>
              ) : (
                <MDBox sx={{ mt: 3 }}>
                  <TableContainer
                    sx={{
                      maxHeight: 500,
                      overflowY: "auto",
                      overflowX: "auto",
                      width: "100%",
                    }}
                  >
                    <Table
                      stickyHeader
                      sx={{
                        tableLayout: "fixed",
                        width: "100%",
                        "& .MuiTableCell-root": {
                          borderBottom: "1px solid #eee",
                        },
                        "& .MuiTableBody-root tr:last-child td": {
                          borderBottom: "none",
                        },
                        "& .MuiTableCell-root": {
                          borderBottom: "1px solid #eee",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          verticalAlign: "top",
                        },
                      }}
                    >
                      {/* HEADER */}
                      <TableHead
                        sx={{ display: "table-header-group !important" }}
                      >
                        <TableRow>
                          {columns.map((col) => (
                            <TableCell
                              key={col.key}
                              sx={{
                                width: `${(col.flex / totalFlex) * 100}%`,
                                px: 1.1,
                                py: 2,
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                verticalAlign: "top",
                              }}
                            >
                              {col.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {tableRows.map((row, idx) => {
                          const isEditing = editingId === row.id;

                          return (
                            <TableRow
                              key={row.id}
                              hover
                              sx={{
                                "&:hover": {
                                  backgroundColor: "#fafafa",
                                },
                              }}
                            >
                              {columns.map((col) => (
                                <TableCell
                                  key={col.key}
                                  sx={{
                                    width: `${(col.flex / totalFlex) * 100}%`,
                                    px: 1.1,
                                    py: 2,
                                  }}
                                >
                                  {col.key === "action" &&
                                  (!isViewMode || isEditEnabled) ? (
                                    isEditing ? (
                                      <>
                                        <IconButton
                                          color="primary"
                                          size="small"
                                          onClick={handleSave}
                                        >
                                          <SaveIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={handleCancelEdit}
                                        >
                                          <CloseIcon fontSize="small" />
                                        </IconButton>
                                      </>
                                    ) : (
                                      <>
                                        <MDBox
                                          display="flex"
                                          alignItems="center"
                                          gap={1}
                                        >
                                          <Icon
                                            sx={({ palette }) => ({
                                              cursor: "pointer",
                                              color: palette.secondary.main,
                                            })}
                                            onClick={() => handleEdit(row)}
                                          >
                                            <i className="fas fa-edit fa-sm" />
                                          </Icon>

                                          <Icon
                                            color="error"
                                            sx={{
                                              cursor: "pointer",
                                              opacity: 0.5,
                                              "&:hover": { opacity: 1 },
                                            }}
                                            onClick={() => handleDeleteRow(row)}
                                          >
                                            <i className="fas fa-trash" />
                                          </Icon>
                                        </MDBox>
                                      </>
                                    )
                                  ) : col.key === "srNo" ? (
                                    idx + 1
                                  ) : col.key === "optimalHours" ? (
                                    isViewMode && !isEditEnabled ? (
                                      row.optimalHours || "-"
                                    ) : (
                                      <Tooltip
                                        title={
                                          isEditing
                                            ? ""
                                            : "Click Edit icon to add optimal hours"
                                        }
                                      >
                                        <span>
                                          <LocalizationProvider
                                            dateAdapter={AdapterDayjs}
                                          >
                                            <ThemeProvider theme={theme}>
                                              <TimePicker
                                                open={
                                                  openPicker.id === row.id &&
                                                  openPicker.field === "optimal"
                                                }
                                                onOpen={() => {
                                                  if (isEditing) {
                                                    setOpenPicker({
                                                      id: row.id,
                                                      field: "optimal",
                                                    });
                                                  }
                                                }}
                                                onClose={() =>
                                                  setOpenPicker({
                                                    id: null,
                                                    field: null,
                                                  })
                                                }
                                                views={["hours", "minutes"]}
                                                format="HH:mm"
                                                ampm={false}
                                                value={
                                                  (
                                                    isEditing
                                                      ? editedRow.optimalHours
                                                      : row.optimalHours
                                                  )
                                                    ? dayjs(
                                                        isEditing
                                                          ? editedRow.optimalHours
                                                          : row.optimalHours,
                                                        "HH:mm",
                                                      )
                                                    : null
                                                }
                                                onChange={(newValue) => {
                                                  if (!isEditing) return;

                                                  const formattedValue =
                                                    newValue
                                                      ? `${String(newValue.hour()).padStart(2, "0")}:${String(
                                                          newValue.minute(),
                                                        ).padStart(2, "0")}`
                                                      : "";

                                                  const actualHours = isEditing
                                                    ? editedRow.hours
                                                    : row.hours;

                                                  if (
                                                    !isOptimalValid(
                                                      actualHours,
                                                      formattedValue,
                                                    )
                                                  ) {
                                                    notify(
                                                      "Optimal hours cannot be greater than actual hours",
                                                      "error",
                                                    );
                                                    return;
                                                  }

                                                  setEditedRow((prev) => ({
                                                    ...prev,
                                                    optimalHours:
                                                      formattedValue,
                                                  }));
                                                }}
                                                slotProps={{
                                                  textField: {
                                                    variant: "standard",
                                                    size: "small",
                                                    fullWidth: true,
                                                    onClick: () => {
                                                      if (!isEditing) {
                                                        notify(
                                                          "Click Edit to modify optimal hours",
                                                          "info",
                                                        );
                                                        return;
                                                      }

                                                      setOpenPicker({
                                                        id: row.id,
                                                        field: "optimal",
                                                      });
                                                    },
                                                    sx: {
                                                      opacity: isEditing
                                                        ? 1
                                                        : 0.6,
                                                      cursor: isEditing
                                                        ? "pointer"
                                                        : "not-allowed",
                                                    },
                                                  },
                                                }}
                                              />
                                            </ThemeProvider>
                                          </LocalizationProvider>
                                        </span>
                                      </Tooltip>
                                    )
                                  ) : isEditing ? (
                                    col.key === "product" ? (
                                      <Autocomplete
                                        {...autoCompleteProps}
                                        options={products}
                                        getOptionLabel={(option) =>
                                          option?.type || ""
                                        }
                                        value={
                                          products?.find(
                                            (p) => p.type === editedRow.product,
                                          ) ?? null
                                        }
                                        onChange={(e, val) =>
                                          handleFieldChangeTable("product", val)
                                        }
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            variant="standard"
                                          />
                                        )}
                                        size="small"
                                      />
                                    ) : col.key === "projectNumber" ? (
                                      <Autocomplete
                                        sx={{
                                          "& .MuiAutocomplete-clearIndicator": {
                                            position: "absolute",
                                            right: "5px", // adjust based on popup icon
                                            top: "0%",
                                            transform: "translateY(-50%)",
                                            zIndex: 1,
                                          },
                                          "& .MuiAutocomplete-endAdornment": {
                                            position: "absolute",
                                            right: "3px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                          },
                                        }}
                                        options={projects}
                                        getOptionLabel={(option) =>
                                          option?.projectNo || ""
                                        }
                                        value={
                                          editedRow.projectNumber
                                            ? projects.find(
                                                (p) =>
                                                  p.projectNo ===
                                                  editedRow.projectNumber,
                                              ) || null
                                            : null
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                          option?.ID === value?.ID
                                        }
                                        onChange={(e, val) =>
                                          handleFieldChangeTable("project", val)
                                        }
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            variant="standard"
                                          />
                                        )}
                                        size="small"
                                      />
                                    ) : col.key === "unitNo" ? (
                                      <Autocomplete
                                        {...autoCompleteProps}
                                        options={editedRow.availableUnits || []}
                                        getOptionLabel={(option) =>
                                          option?.unitNo || ""
                                        }
                                        value={
                                          (editedRow.availableUnits || []).find(
                                            (u) =>
                                              String(u.ID) ===
                                              String(editedRow.unitId),
                                          ) || null
                                        }
                                        onChange={(e, val) =>
                                          handleFieldChangeTable("unitNo", val)
                                        }
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            variant="standard"
                                          />
                                        )}
                                        size="small"
                                        disabled={!editedRow.projectNumber}
                                      />
                                    ) : col.key === "stationName" ? (
                                      <Autocomplete
                                        sx={{
                                          "& .MuiAutocomplete-clearIndicator": {
                                            position: "absolute",
                                            right: "5px", // adjust based on popup icon
                                            top: "0%",
                                            transform: "translateY(-50%)",
                                            zIndex: 1,
                                          },
                                          "& .MuiAutocomplete-endAdornment": {
                                            position: "absolute",
                                            right: "3px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                          },
                                        }}
                                        {...autoCompleteProps}
                                        options={stations}
                                        getOptionLabel={(option) =>
                                          option?.name || ""
                                        }
                                        value={
                                          stations.find(
                                            (s) => s.ID === editedRow.stationId,
                                          ) || null
                                        }
                                        onChange={(e, val) =>
                                          handleFieldChangeTable(
                                            "stationName",
                                            val,
                                          )
                                        }
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            variant="standard"
                                          />
                                        )}
                                        size="small"
                                      />
                                    ) : col.key === "subActivityName" ? (
                                      <Autocomplete
                                        sx={{
                                          "& .MuiAutocomplete-clearIndicator": {
                                            position: "absolute",
                                            right: "5px", // adjust based on popup icon
                                            top: "0%",
                                            transform: "translateY(-50%)",
                                            zIndex: 1,
                                          },
                                          "& .MuiAutocomplete-endAdornment": {
                                            position: "absolute",
                                            right: "3px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                          },
                                        }}
                                        {...autoCompleteProps}
                                        options={subActivitiesList}
                                        getOptionLabel={(option) =>
                                          option?.name || ""
                                        }
                                        value={
                                          subActivitiesList.find(
                                            (s) =>
                                              String(s.ID) ===
                                              String(editedRow.subActivity),
                                          ) || null
                                        }
                                        onChange={(e, val) =>
                                          handleFieldChangeTable(
                                            "subActivity",
                                            val?.ID || null,
                                          )
                                        }
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            variant="standard"
                                          />
                                        )}
                                        size="small"
                                      />
                                    ) : col.key === "microActivityName" ? (
                                      <Autocomplete
                                        sx={{
                                          "& .MuiAutocomplete-clearIndicator": {
                                            position: "absolute",
                                            right: "5px", // adjust based on popup icon
                                            top: "0%",
                                            transform: "translateY(-50%)",
                                            zIndex: 1,
                                          },
                                          "& .MuiAutocomplete-endAdornment": {
                                            position: "absolute",
                                            right: "3px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                          },
                                        }}
                                        {...autoCompleteProps}
                                        options={microActivitesList.filter(
                                          (m) =>
                                            String(m.subActivity?.ID) ===
                                            String(editedRow.subActivity),
                                        )}
                                        getOptionLabel={(option) =>
                                          option?.name || ""
                                        }
                                        value={
                                          microActivitesList.find(
                                            (m) =>
                                              String(m.ID) ===
                                              String(editedRow.microActivity),
                                          ) || null
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                          String(option.ID) === String(value.ID)
                                        }
                                        onChange={(e, val) =>
                                          handleFieldChangeTable(
                                            "microActivity",
                                            val?.ID || null,
                                          )
                                        }
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            variant="standard"
                                          />
                                        )}
                                        size="small"
                                        disabled={!editedRow.subActivity}
                                      />
                                    ) : col.key === "hours" ? (
                                      <LocalizationProvider
                                        dateAdapter={AdapterDayjs}
                                      >
                                        <ThemeProvider theme={theme}>
                                          <TimePicker
                                            open={
                                              openPicker.id === row.id &&
                                              openPicker.field === "hours"
                                            }
                                            onOpen={() => {
                                              if (isEditing) {
                                                setOpenPicker({
                                                  id: row.id,
                                                  field: "hours",
                                                });
                                              }
                                            }}
                                            onClose={() =>
                                              setOpenPicker({
                                                id: null,
                                                field: null,
                                              })
                                            }
                                            views={["hours", "minutes"]}
                                            format="HH:mm"
                                            ampm={false}
                                            value={
                                              editedRow.hours
                                                ? dayjs(
                                                    editedRow.hours,
                                                    "HH:mm",
                                                  )
                                                : null
                                            }
                                            onChange={(newValue) =>
                                              handleTimeChange(newValue)
                                            }
                                            slotProps={{
                                              textField: {
                                                variant: "standard",
                                                size: "small",
                                                fullWidth: true,
                                                onClick: () => {
                                                  if (!isEditing) {
                                                    notify(
                                                      "Click Edit to modify hours",
                                                      "info",
                                                    );
                                                    return;
                                                  }
                                                  setOpenPicker({
                                                    id: row.id,
                                                    field: "hours",
                                                  });
                                                },
                                                sx: {
                                                  cursor: isEditing
                                                    ? "pointer"
                                                    : "not-allowed",
                                                },
                                              },
                                            }}
                                          />
                                        </ThemeProvider>
                                      </LocalizationProvider>
                                    ) : col.key === "activityType" ? (
                                      <Autocomplete
                                        sx={{
                                          "& .MuiAutocomplete-clearIndicator": {
                                            position: "absolute",
                                            right: "5px", // adjust based on popup icon
                                            top: "0%",
                                            transform: "translateY(-50%)",
                                            zIndex: 1,
                                          },
                                          "& .MuiAutocomplete-endAdornment": {
                                            position: "absolute",
                                            right: "3px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                          },
                                        }}
                                        options={activityOptions}
                                        getOptionLabel={(option) =>
                                          option.label
                                        }
                                        value={
                                          activityOptions.find(
                                            (o) =>
                                              o.value ===
                                              editedRow.activityType,
                                          ) || null
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                          option.value === value.value
                                        }
                                        onChange={(e, val) =>
                                          handleFieldChangeTable(
                                            "activityType",
                                            val?.value || "",
                                          )
                                        }
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            variant="standard"
                                          />
                                        )}
                                        size="small"
                                      />
                                    ) : col.key === "remark" ? (
                                      <Autocomplete
                                        sx={{
                                          "& .MuiAutocomplete-clearIndicator": {
                                            position: "absolute",
                                            right: "5px",
                                            top: "0%",
                                            transform: "translateY(-50%)",
                                            zIndex: 1,
                                          },
                                          "& .MuiAutocomplete-endAdornment": {
                                            position: "absolute",
                                            right: "3px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                          },
                                        }}
                                        {...autoCompleteProps}
                                        options={remarkslist}
                                        getOptionLabel={(option) =>
                                          option?.remark || ""
                                        }
                                        value={
                                          editedRow.customRemark
                                            ? null
                                            : remarkslist.find(
                                                (r) =>
                                                  String(r.ID) ===
                                                  String(editedRow.remarkId),
                                              ) || null
                                        }
                                        inputValue={editedRow.remark || ""}
                                        onInputChange={(
                                          event,
                                          newInputValue,
                                          reason,
                                        ) => {
                                          if (reason === "input") {
                                            setEditedRow((prev) => ({
                                              ...prev,
                                              remark: newInputValue,
                                              customRemark: newInputValue,
                                              remarkId: null,
                                            }));
                                          }
                                        }}
                                        onChange={(e, val) => {
                                          if (val) {
                                            setEditedRow((prev) => ({
                                              ...prev,
                                              remark: val.remark || "",
                                              customRemark: "",
                                              remarkId: val.ID || null,
                                            }));
                                          } else {
                                            setEditedRow((prev) => ({
                                              ...prev,
                                              remark: "",
                                              customRemark: "",
                                              remarkId: null,
                                            }));
                                          }
                                        }}
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            variant="standard"
                                            placeholder="Select or type remark"
                                          />
                                        )}
                                        size="small"
                                        freeSolo
                                      />
                                    ) : (
                                      <TextField
                                        variant="standard"
                                        value={editedRow[col.key] || ""}
                                        onChange={(e) =>
                                          handleFieldChangeTable(
                                            col.key,
                                            e.target.value,
                                          )
                                        }
                                        size="small"
                                        fullWidth
                                      />
                                    )
                                  ) : (
                                    row[col.key]
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <MDBox
                    sx={{
                      mt: 2,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <MDBox
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      {/* Total Hours */}
                      <MDTypography fontWeight="bold" fontSize="1.3rem">
                        Total Hours: {formattedTotal}
                      </MDTypography>

                      {/* Buttons */}
                      {(!isViewMode || isEditEnabled) && (
                        <MDBox display="flex" gap={2}>
                          <MDButton
                            variant="outlined"
                            color="success"
                            onClick={handleApprove}
                          >
                            Approve
                          </MDButton>

                          <MDButton
                            variant="outlined"
                            color="error"
                            onClick={handleReject}
                          >
                            Reject
                          </MDButton>
                        </MDBox>
                      )}
                    </MDBox>
                  </MDBox>
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default LHBDataDetails;
